import { axiosGet, axiosPost } from 'Redux/axiosHelper'
import {
  McpConsentPayload,
  McpConsentRequest,
  McpConsentResult,
} from 'types/mcpOAuth/mcpOAuth'

/**
 * The AI assistant connection endpoints.
 *
 * These are the human half of an OAuth flow that starts inside ChatGPT or Claude. The protocol half
 * — discovery, client registration, the token exchange — is spoken by the AI client directly to
 * `/mcp/oauth/*` on the API and never passes through this app.
 *
 * `axiosGet`/`axiosPost` attach the Firebase ID token and `bam_app` automatically, which is exactly
 * what we want: the API needs to know WHICH logged-in person is approving, and that is what turns
 * an anonymous connection request into a scoped token.
 */

/**
 * Read a pending connection request.
 *
 * @param requestId the `request_id` the API put in the URL when it redirected here
 */
export const getMcpConsentRequest = async (
  requestId: string,
): Promise<McpConsentRequest> => {
  const res = await axiosGet(`v1/mcp-oauth/requests/${requestId}`)
  return res.data
}

/**
 * Approve the connection.
 *
 * Returns the URL to send the browser to — the authorization code travels in it, so navigate,
 * never fetch.
 *
 * @param payload which request, which identity, and the scopes actually ticked
 */
export const approveMcpConsent = async (
  payload: McpConsentPayload,
): Promise<McpConsentResult> => {
  // `showMessage: false` — axiosPost pops a generic "Data Added Successfully!" toast by default,
  // which is wrong for a permission decision. The screen says what happened in its own words.
  const res = await axiosPost(payload, `v1/mcp-oauth/consent`, undefined, false)
  return res.data
}

/**
 * Decline the connection.
 *
 * Tells the waiting AI client explicitly, so it stops with a clear message instead of timing out.
 *
 * @param requestId the pending request
 */
export const denyMcpConsent = async (
  requestId: string,
): Promise<McpConsentResult> => {
  const res = await axiosPost({ requestId }, `v1/mcp-oauth/deny`, undefined, false)
  return res.data
}
